import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { Button } from '@components/ui/button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/shadcn/dialog';
import { Label } from '@components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/shadcn/select';
import { RadioGroup, RadioGroupItem } from '@components/ui/shadcn/radio-group';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CountryModel } from '../../models/Country';
import { CourierModel, ServiceModel } from '../../models/Courier';
import { useCouriers, useCourierServices } from '../../hooks';

interface ExportCoverageRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { countryId: number; courierServiceId?: number }) => void;
  isExporting?: boolean;
  countries: CountryModel[];
}

export const ExportCoverageRulesModal = ({
  isOpen,
  onClose,
  onSubmit,
  isExporting = false,
  countries,
}: ExportCoverageRulesModalProps) => {
  const { t } = useTranslation('coverageZonesModule');
  const [exportType, setExportType] = useState<'all' | 'service'>('all');
  const [countryId, setCountryId] = useState<string>('');
  const [courierId, setCourierId] = useState<string>('');
  const [serviceId, setServiceId] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Obtener couriers
  const { couriers } = useCouriers();

  // Obtener servicios del courier seleccionado
  const { services } = useCourierServices({
    courierId: courierId || '',
    enabled: !!courierId && exportType === 'service',
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setExportType('all');
      setCountryId('');
      setCourierId('');
      setServiceId('');
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Reset submitting state when export finishes
  useEffect(() => {
    if (!isExporting && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isExporting, isSubmitting]);

  // Reset service when courier changes
  useEffect(() => {
    if (exportType === 'service') {
      setServiceId('');
    }
  }, [courierId, exportType]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!countryId) {
      newErrors.country_id = t('validation.countryRequired');
    }

    if (exportType === 'service' && !serviceId) {
      newErrors.service_id = t('export.serviceRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Prevenir doble envío
    if (isSubmitting || isExporting) return;

    if (!validate()) return;

    setIsSubmitting(true);
    onSubmit({
      countryId: parseInt(countryId),
      courierServiceId: exportType === 'service' && serviceId ? parseInt(serviceId) : undefined,
    });
  };

  const handleClose = () => {
    if (!isExporting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto transition-colors">
        <DialogHeader>
          <DialogTitle className="text-foreground">{t('export.title')}</DialogTitle>
          <DialogDescription className="text-muted-foreground">{t('export.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tipo de exportación */}
          <div className="space-y-3">
            <Label className="text-foreground font-medium">{t('export.exportType')}</Label>
            <RadioGroup
              value={exportType}
              onValueChange={(value) => setExportType(value as 'all' | 'service')}
              disabled={isExporting}
              className="space-y-3"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="export-all" />
                <Label htmlFor="export-all" className="text-foreground font-normal cursor-pointer">
                  {t('export.exportAll')}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="service" id="export-service" />
                <Label htmlFor="export-service" className="text-foreground font-normal cursor-pointer">
                  {t('export.exportByService')}
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* País */}
          <div className="space-y-2">
            <Label htmlFor="country_id" className="text-foreground">
              {t('form.country')} <span className="text-destructive">*</span>
            </Label>
            <Combobox
              options={countries.map((c) => ({
                label: c.name,
                value: String(c.id),
                searchValue: `${c.name} ${c.iso2 || ''} ${c.iso3 || ''}`.trim(),
              }))}
              placeholder={t('form.selectCountry')}
              value={countryId}
              setValue={setCountryId}
              disabled={isExporting}
            />
            {errors.country_id && <p className="text-sm text-destructive">{errors.country_id}</p>}
          </div>

          {/* Courier y Servicio (solo si exportType === 'service') */}
          {exportType === 'service' && (
            <>
              {/* Courier */}
              <div className="space-y-2">
                <Label htmlFor="courier_id" className="text-foreground">
                  {t('export.courier')} <span className="text-destructive">*</span>
                </Label>
                <Select value={courierId} onValueChange={setCourierId} disabled={isExporting}>
                  <SelectTrigger id="courier_id">
                    <SelectValue placeholder={t('export.selectCourier')} />
                  </SelectTrigger>
                  <SelectContent>
                    {couriers.map((courier) => (
                      <SelectItem key={courier.id} value={courier.id}>
                        {courier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Servicio */}
              {courierId && (
                <div className="space-y-2">
                  <Label htmlFor="service_id" className="text-foreground">
                    {t('export.service')} <span className="text-destructive">*</span>
                  </Label>
                  <Select value={serviceId} onValueChange={setServiceId} disabled={isExporting || !courierId}>
                    <SelectTrigger id="service_id">
                      <SelectValue placeholder={t('export.selectService')} />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id.toString()}>
                          {service.code} - {service.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.service_id && <p className="text-sm text-destructive">{errors.service_id}</p>}
                </div>
              )}
            </>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isExporting}
              className="transition-colors"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isExporting || isSubmitting}
              className="transition-colors"
            >
              {isExporting || isSubmitting ? t('export.exporting') : t('export.export')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
